import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Surface, Text } from '@/components/flup';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import {
  getGoLiveQuote,
  getMonetisationPlans,
} from '@/store/thunkMidddleware/monetisationMiddleware';
import {
  BillingCycle,
  CyclePricing,
  SelectablePlan,
  formatPaise,
} from '@/types/monetisationTypes';

/**
 * The go-live paywall.
 *
 * Two ways to pay for a venue — a commission on each booking, or a fixed
 * subscription with no commission — chosen here rather than discovered after
 * the first booking.
 *
 * Every figure comes from /api/monetisation/plans; nothing is hardcoded, so a
 * price or GST change on the server lands here without a release. Picking the
 * subscription adds that cycle to today's payment because the listing fee rides
 * on the subscription's first debit as an addon.
 */

export interface PlanSelection {
  /** FOUNDING_PARTNER is granted, never picked here — only these two are choosable. */
  plan: SelectablePlan;
  billingCycle: BillingCycle | null;
}

interface GoLiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: string | null;
  venueName?: string;
  onPay: (selection: PlanSelection) => void;
}

const SummaryRow: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
  note?: string;
}> = ({ label, value, strong, note }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <Text variant={strong ? 'bodySemi' : 'body'} tone={strong ? 'primary' : 'secondary'} as="p">
        {label}
      </Text>
      {note ? (
        <Text variant="micro" tone="tertiary" as="p">
          {note}
        </Text>
      ) : null}
    </div>
    <Text variant={strong ? 'bodySemi' : 'body'} as="span" className="nums shrink-0">
      {value}
    </Text>
  </div>
);

const GoLiveModal: React.FC<GoLiveModalProps> = ({
  open,
  onOpenChange,
  venueId,
  venueName,
  onPay,
}) => {
  const dispatch = useAppDispatch();
  const { plansResponse, goLiveQuoteResponse } = useAppSelector(
    (state: RootState) => state.monetisation,
  );

  const [plan, setPlan] = useState<SelectablePlan>('COMMISSION');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const plans = plansResponse?.payload;
  const onboarding = plans?.onboarding;
  const commission = plans?.commission;
  const subscription = plans?.subscription;
  const cycles: CyclePricing[] = subscription?.cycles || [];

  useEffect(() => {
    if (!open) return;
    // Reopening for another venue must not inherit the last one's choice.
    setPlan('COMMISSION');
    setBillingCycle('monthly');
    dispatch(getMonetisationPlans({}));
    if (venueId) dispatch(getGoLiveQuote({ venueId }));
  }, [open, venueId]);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.billingCycle === billingCycle),
    [cycles, billingCycle],
  );
  const monthlyCycle = useMemo(
    () => cycles.find((c) => c.billingCycle === 'monthly'),
    [cycles],
  );

  // goLiveQuote is authoritative for the listing-fee side; /plans is the
  // fallback so the modal still prices correctly if that call fails.
  const onboardingPayable =
    goLiveQuoteResponse?.payload?.payTodayPaise ?? onboarding?.payablePaise ?? 0;

  const payTodayPaise =
    plan === 'SUBSCRIPTION'
      ? onboardingPayable + (selectedCycle?.payablePaise ?? 0)
      : onboardingPayable;

  const loading = plansResponse?.isLoading || goLiveQuoteResponse?.isLoading;
  const canPay = Boolean(plans) && !loading;

  const planCard = (selected: boolean) =>
    cn(
      'w-full text-left rounded-lg border p-4 transition-colors',
      selected
        ? 'border-flup-brand bg-flup-brand-soft'
        : 'border-flup-hairline hover:bg-flup-surface-alt',
    );

  const radioDot = (selected: boolean) =>
    cn(
      'grid place-items-center w-5 h-5 rounded-pill border shrink-0 mt-0.5 transition-colors',
      selected ? 'border-flup-brand bg-flup-brand text-white' : 'border-flup-hairline-2',
    );

  const cycleButton = (selected: boolean) =>
    cn(
      'rounded-lg border p-3 text-left transition-colors',
      selected
        ? 'border-flup-brand bg-flup-surface'
        : 'border-flup-hairline hover:bg-flup-surface-alt',
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venueName ? `Go live: ${venueName}` : 'Go live'}</DialogTitle>
          <DialogDescription>Choose how you want to pay. You can switch anytime.</DialogDescription>
        </DialogHeader>

        {loading && !plans ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-flup-brand" aria-label="Loading plans" />
          </div>
        ) : !plans ? (
          <Surface padding="p-4" className="bg-flup-surface-alt">
            <Text variant="body" tone="secondary" as="p">
              {plansResponse?.error || 'Could not load the plans. Please close this and try again.'}
            </Text>
          </Surface>
        ) : (
          <>
            <div role="radiogroup" aria-label="Payment plan" className="space-y-3">
              <button
                type="button"
                role="radio"
                aria-checked={plan === 'COMMISSION'}
                onClick={() => setPlan('COMMISSION')}
                className={planCard(plan === 'COMMISSION')}
              >
                <div className="flex items-start gap-3">
                  <span className={radioDot(plan === 'COMMISSION')}>
                    {plan === 'COMMISSION' ? <Check className="w-3 h-3" aria-hidden /> : null}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Text variant="bodySemi" as="span">
                          Pay per booking
                        </Text>
                        <span className="rounded-pill bg-flup-info-soft px-2 py-0.5">
                          <Text variant="micro" tone="info" as="span">
                            Default
                          </Text>
                        </span>
                      </div>
                      <Text variant="bodySemi" as="span" className="nums shrink-0">
                        {formatPaise(commission?.monthlyFeePaise)}/mo
                      </Text>
                    </div>
                    <Text variant="caption" tone="secondary" as="p" className="mt-1">
                      {commission?.commissionPercent}% deducted per booking. No monthly cost — you
                      pay only when you earn.
                    </Text>
                  </div>
                </div>
              </button>

              <div className={planCard(plan === 'SUBSCRIPTION')}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={plan === 'SUBSCRIPTION'}
                  onClick={() => setPlan('SUBSCRIPTION')}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <span className={radioDot(plan === 'SUBSCRIPTION')}>
                      {plan === 'SUBSCRIPTION' ? <Check className="w-3 h-3" aria-hidden /> : null}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <Text variant="bodySemi" as="span">
                          Zero commission
                        </Text>
                        <div className="shrink-0 text-right">
                          <Text variant="bodySemi" as="p" className="nums">
                            {formatPaise(monthlyCycle?.netPricePaise)}/mo
                          </Text>
                          {monthlyCycle?.gstPaise ? (
                            <Text variant="micro" tone="tertiary" as="p">
                              + {monthlyCycle.gstPercent}% GST
                            </Text>
                          ) : null}
                        </div>
                      </div>
                      <Text variant="caption" tone="secondary" as="p" className="mt-1">
                        Keep 100% of every booking. Fixed fee per cycle, unlimited bookings.
                      </Text>
                    </div>
                  </div>
                </button>

                <div className="grid grid-cols-2 gap-2 mt-3 sm:ml-8">
                  {cycles.map((cycle) => {
                    const selected = plan === 'SUBSCRIPTION' && billingCycle === cycle.billingCycle;
                    return (
                      <button
                        key={cycle.billingCycle}
                        type="button"
                        onClick={() => {
                          setPlan('SUBSCRIPTION');
                          setBillingCycle(cycle.billingCycle);
                        }}
                        className={cycleButton(selected)}
                      >
                        <Text
                          variant="labelSemi"
                          as="p"
                          tone={cycle.discountPercent ? 'green' : 'primary'}
                          className="capitalize"
                        >
                          {cycle.billingCycle}
                          {cycle.discountPercent ? ` · save ${cycle.discountPercent}%` : ''}
                        </Text>
                        <Text variant="caption" tone="secondary" as="p" className="nums mt-0.5">
                          {formatPaise(cycle.netPricePaise)} /{' '}
                          {cycle.durationInMonths === 12 ? 'year' : 'month'}
                        </Text>
                        {cycle.gstPaise ? (
                          <Text variant="micro" tone="tertiary" as="p" className="nums mt-0.5">
                            + {formatPaise(cycle.gstPaise)} GST
                          </Text>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <Surface padding="p-4" className="space-y-2 bg-flup-surface-alt">
              <SummaryRow
                label={onboarding?.label || 'One-time listing fee'}
                value={formatPaise(onboarding?.listingFeePaise)}
              />

              {onboarding?.directBookingLinkPaise ? (
                <SummaryRow
                  label="Direct booking link (lifetime)"
                  note={onboarding.gstApplicable ? 'Includes GST' : undefined}
                  value={formatPaise(onboarding.directBookingLinkPaise)}
                />
              ) : null}

              {plan === 'SUBSCRIPTION' && selectedCycle ? (
                <>
                  <SummaryRow
                    label={`Zero commission (${selectedCycle.billingCycle})`}
                    value={formatPaise(selectedCycle.basePricePaise)}
                  />
                  {selectedCycle.discountPaise ? (
                    <SummaryRow
                      label={selectedCycle.discountLabel || 'Discount'}
                      value={`− ${formatPaise(selectedCycle.discountPaise)}`}
                    />
                  ) : null}
                </>
              ) : null}

              {/* GST is configurable and currently zero, so the row only shows
                  when there is something to show. */}
              {onboarding?.gstPaise || selectedCycle?.gstPaise ? (
                <SummaryRow
                  label={`GST (${onboarding?.gstPercent ?? 0}%)`}
                  note={
                    onboarding?.directBookingLinkPaise
                      ? plan === 'SUBSCRIPTION'
                        ? 'On the listing fee and subscription'
                        : 'On the listing fee'
                      : undefined
                  }
                  value={formatPaise(
                    (onboarding?.gstPaise ?? 0) +
                      (plan === 'SUBSCRIPTION' ? selectedCycle?.gstPaise ?? 0 : 0),
                  )}
                />
              ) : null}

              <div className="border-t border-flup-hairline pt-2">
                <SummaryRow label="Pay today" value={formatPaise(payTodayPaise)} strong />
              </div>

              {plan === 'SUBSCRIPTION' && selectedCycle ? (
                <Text variant="caption" tone="tertiary" as="p" className="pt-1">
                  Renews at {formatPaise(selectedCycle.payablePaise)}
                  {selectedCycle.gstPaise ? ' (incl. GST)' : ''} every{' '}
                  {selectedCycle.durationInMonths === 12
                    ? 'year'
                    : `${selectedCycle.durationInMonths} month${
                        selectedCycle.durationInMonths > 1 ? 's' : ''
                      }`}{' '}
                  by UPI Autopay. You will authorise the mandate at checkout.
                </Text>
              ) : null}
            </Surface>

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
              <Button
                fullWidth
                disabled={!canPay}
                onClick={() =>
                  onPay({ plan, billingCycle: plan === 'SUBSCRIPTION' ? billingCycle : null })
                }
              >
                Pay {formatPaise(payTodayPaise)} and go live
              </Button>
              <Text variant="caption" tone="tertiary" as="p" className="text-center">
                Listing fee is one-time and non-refundable.
                {plan === 'COMMISSION'
                  ? ` ${commission?.commissionPercent}% applies from your first booking.`
                  : ` No commission is taken while the subscription is active.`}
              </Text>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GoLiveModal;
